import { Blocks, Shapes } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

export type SchemaGap = 'noSchema' | 'noTypes' | 'missingType' | 'missingRelation'

interface SchemaGapNoticeProps {
  caseId: string
  gap: SchemaGap
  /** For `missingRelation`: the two types the schema has no pattern between. */
  sourceType?: string
  targetType?: string
  /** True when the schema offers no relation at all for the pair, POSSIBLE_MATCH aside. */
  onlySystemRelation?: boolean
  className?: string
}

/** Where to send someone whose schema lacks what they want to add. */
function schemaBuilderPath(caseId: string): string {
  return `/cases/${caseId}/schema`
}

/**
 * The schema decides what may be added by hand; when it lacks a type or a
 * relationship, this says so and opens the Schema Builder, where the analyst
 * adds it by hand or with the schema agent.
 */
export function SchemaGapNotice({
  caseId,
  gap,
  sourceType,
  targetType,
  onlySystemRelation,
  className,
}: SchemaGapNoticeProps) {
  const { t } = useTranslation()
  const blocking = gap === 'noSchema' || gap === 'noTypes'

  if (blocking) {
    return (
      <div className={cn('flex flex-col items-center gap-4 px-2 py-6 text-center', className)}>
        <span className="flex size-11 items-center justify-center rounded-xl border border-border bg-muted">
          <Shapes className="size-5 text-muted-foreground" aria-hidden />
        </span>
        <div className="max-w-sm space-y-1.5">
          <p className="text-sm font-semibold text-foreground">
            {t(`manualGraph.schemaGap.${gap}Title`)}
          </p>
          <p className="text-sm text-muted-foreground">{t('manualGraph.schemaGap.body')}</p>
        </div>
        <Button asChild variant="solid">
          <Link to={schemaBuilderPath(caseId)}>
            <Blocks className="size-4" aria-hidden />
            {t('manualGraph.schemaGap.openSchema')}
          </Link>
        </Button>
      </div>
    )
  }

  const body =
    gap === 'missingType'
      ? t('manualGraph.schemaGap.missingTypeBody')
      : onlySystemRelation
        ? t('manualGraph.relationship.noneBody', { source: sourceType, target: targetType })
        : t('manualGraph.relationship.missingBody', { source: sourceType, target: targetType })

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/40 p-3 sm:flex-row sm:items-center',
        className
      )}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium text-foreground">
          {gap === 'missingType'
            ? t('manualGraph.schemaGap.missingTypeTitle')
            : t('manualGraph.relationship.missingTitle')}
        </p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0">
        <Link to={schemaBuilderPath(caseId)}>
          <Blocks className="size-4" aria-hidden />
          {t('manualGraph.schemaGap.openSchema')}
        </Link>
      </Button>
    </div>
  )
}
